import cond from 'lodash/cond';
import stubTrue from 'lodash/stubTrue';

import { GOLDCAST_PROVISIONING_STATUS } from 'common/utils/goldcastEligibility';

const { READY, PENDING, NOT_ELIGIBLE } = GOLDCAST_PROVISIONING_STATUS;

// TODO: Rename after relfix: NOT_ELIGIBLE = can't sync to Goldcast, INELIGIBLE = can't select as active embed.
export const GOLDCAST_DISABLED_REASON = {
    NOT_ELIGIBLE: 'not_eligible',
    PENDING: 'pending',
    BULK_UPLOAD: 'bulk_upload',
    INELIGIBLE: 'ineligible',
};

const getState = (state = {}) => ({
    isHidden: false,
    isDisabled: false,
    isPending: false,
    disabledReason: null,
    ...state,
});

/**
 * How the Goldcast option shows in the embed dropdown (the alt embed lock is applied on top by the dropdown).
 *
 * The provisioning status decides; an unknown or missing status hides the option. `hasGoldcastIds` (the bootstrap
 * `goldcastVirtualEvent` has gcEventId and gcBroadcastId) only narrows `ready` and never enables the option, since
 * the ids outlive eligibility: once the Goldcast integration config is disabled or stops matching the event, its
 * registrants are no longer synced and the status is `not_eligible`. `ready` without ids means setup finished after
 * page load: it shows as pending until a reload, as the Studio settings tabs read the ids from the bootstrap.
 *
 * An already selected Goldcast stays selectable when it is `not_eligible` or `canSelect` is false, so a saved
 * Goldcast embed is kept as is.
 */
export const getGoldcastOptionState = ({
    goldcastEligibility,
    hasGoldcastIds = false,
    isBulkUploadDisabled = false,
    isGoldcastSelected = false,
}) => {
    const status = goldcastEligibility?.provisioningStatus;
    const isProvisioning = status === PENDING || (status === READY && !hasGoldcastIds);
    const disabledBy = (disabledReason) => () => getState({ isDisabled: true, disabledReason });

    return cond([
        [() => status === NOT_ELIGIBLE && isGoldcastSelected, () => getState()],
        [() => status === NOT_ELIGIBLE, disabledBy(GOLDCAST_DISABLED_REASON.NOT_ELIGIBLE)],
        [
            () => isProvisioning,
            () => getState({ isDisabled: true, isPending: true, disabledReason: GOLDCAST_DISABLED_REASON.PENDING }),
        ],
        [() => status !== READY, () => getState({ isHidden: true })],
        [() => isBulkUploadDisabled, disabledBy(GOLDCAST_DISABLED_REASON.BULK_UPLOAD)],
        [
            () => goldcastEligibility?.canSelect === false && !isGoldcastSelected,
            disabledBy(GOLDCAST_DISABLED_REASON.INELIGIBLE),
        ],
        [stubTrue, () => getState()],
    ])();
};
